import React, { Fragment, useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Table, Button, Spinner } from 'reactstrap';
import { FileText, Download, Edit2 } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Breadcrumbs } from '../../AbstractElements';
import axios from 'axios';
import API_BASE_URL from '../../Config/api';
import { useOrderStatuses, isCustomerEditableStatus, isApprovedStatus } from '../../Config/orderStatuses';
import authHeader from './authHeader';

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');

const MyOrders = () => {
  useOrderStatuses(); // loads the shared status rules and re-renders when they arrive
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/customer/orders`, authHeader());
        setOrders(res.data.orders || []);
      } catch (e) {
        console.error('Failed to load orders', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Download the invoice / proforma PDF as a blob so the JWT auth header is sent, then open it.
  const downloadDoc = async (orderId, type) => {
    const key = `${orderId}-${type}`;
    setDownloading(key);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/customer/orders/${orderId}/${type}`,
        { ...authHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const win = window.open(url, '_blank');
      if (!win) {
        const a = document.createElement('a');
        a.href = url; a.download = `${type === 'invoice' ? 'Invoice' : 'PI'}_${orderId}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      let msg = 'Could not open the document. Please try again.';
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).error || msg; } catch (_) { /* default */ }
      }
      toast.error(msg);
    } finally {
      setDownloading(null);
    }
  };

  const renderDoc = (o) => {
    if (o.invoice_generated) {
      const key = `${o.order_id}-invoice`;
      return (
        <Button color="primary" size="sm" disabled={downloading === key} onClick={() => downloadDoc(o.order_id, 'invoice')}>
          {downloading === key ? <Spinner size="sm" /> : <FileText size={14} />}<span className="ms-1">Invoice</span>
        </Button>
      );
    }
    const key = `${o.order_id}-proforma`;
    const isApproved = isApprovedStatus(o.status || 'New Order');
    return (
      <Button
        color="secondary"
        size="sm"
        outline
        disabled={!isApproved || downloading === key}
        onClick={() => isApproved && downloadDoc(o.order_id, 'proforma')}
        title={!isApproved ? 'Awaiting admin approval' : 'Download Proforma'}
      >
        {downloading === key ? <Spinner size="sm" /> : <Download size={14} />}<span className="ms-1">Proforma</span>
      </Button>
    );
  };

  return (
    <Fragment>
      <Breadcrumbs parent="Dashboard" title="Orders" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader className="pb-0"><h5>Orders</h5></CardHeader>
              <CardBody>
                {loading ? (
                  <p className="text-muted">Loading...</p>
                ) : orders.length === 0 ? (
                  <p className="text-muted">You have no orders yet.</p>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-border-horizontal align-middle">
                      <thead>
                        <tr>
                          <th>Order ID</th><th>Store</th><th>Order Date</th>
                          <th>Delivery</th><th>Items</th><th>Amount</th><th>Status</th><th>Document</th><th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((o) => {
                          const editable = isCustomerEditableStatus(o.status) && !o.is_shipped;
                          const isApproved = isApprovedStatus(o.status || 'New Order');
                          return (
                            <tr key={o.order_id}>
                              <td><strong>{o.order_id}</strong></td>
                              <td>{o.store_name || '-'}</td>
                              <td>{formatDate(o.order_date)}</td>
                              <td>{formatDate(o.preferred_delivery_date)}</td>
                              <td>{o.item_count || 0}</td>
                              <td>{`${o.currency === 'USD' ? '$' : '€'}${Number(o.total_amount || 0).toFixed(2)}`}</td>
                              <td>
                            {o.is_shipped ? 'Shipped' : (o.status || 'New')}
                            {o.status_changed_at && !Number.isNaN(new Date(o.status_changed_at).getTime()) && (
                              <div className="text-muted" style={{ fontSize: '11px' }}>
                                since {new Date(o.status_changed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </div>
                            )}
                          </td>
                              <td>{renderDoc(o)}</td>
                              <td>
                                {editable ? (
                                  <Button color="warning" size="sm" outline disabled={isApproved} onClick={() => !isApproved && navigate(`/customer/orders/${o.order_id}/edit`)} title={isApproved ? 'Order approved — editing locked' : 'Edit order'}>
                                    <Edit2 size={13} /><span className="ms-1">Edit</span>
                                  </Button>
                                ) : <span className="text-muted f-12">Locked</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default MyOrders;
